import './FilterBarHelp.scss';
import { ReactNode, useMemo, useState } from 'react';
import { Carousel, Modal, Table } from 'react-bootstrap';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useStateFunction } from '../../utils/hooks/useData';
import { Button } from '../Button';
import { Appearance, Size } from '../designConstants';
import { InteractiveDiv } from '../InteractiveEl';
import { AdvancedFilterBar } from './AdvancedFilterBar';
import { BasicFilterBar } from './BasicFilterBar';

const NUM_SLIDES = 8;

const SAMPLE_OPTIONS = ['Creative', 'Network', 'Spend'];
const SAMPLE_NETWORKS = ['HULUCOMEDY', 'MTV', 'MTV2', 'NBCCOMEDY', 'NBCFEM'];
const SAMPLE_CREATIVES = ['Celebrity', 'Sale'];

const SAMPLE_DATA: Record<string, string>[] = [];
for (const Network of SAMPLE_NETWORKS) {
  for (const Creative of SAMPLE_CREATIVES) {
    SAMPLE_DATA.push({
      Network,
      Creative,
      Spend: `$${(500 + Math.floor(Math.random() * 1000)).toLocaleString()}`
    });
  }
}

const FilterDemo = ({
  advanced = false,
  data
}: {
  advanced?: boolean;
  data: Record<string, string>[];
}) => {
  const [filterFn, setFilterFn] = useStateFunction<(line: Record<string, string>) => boolean>(
    () => true
  );
  const filteredData = useMemo(() => data.filter(filterFn), [filterFn, data]);

  return (
    <div className='sample-table'>
      <div className='filter-bar-component-container large' style={{ width: 400 }}>
        {advanced ? (
          <AdvancedFilterBar lines={data} onFilter={setFilterFn} options={SAMPLE_OPTIONS} />
        ) : (
          <BasicFilterBar onFilter={setFilterFn} options={SAMPLE_OPTIONS} />
        )}
      </div>
      <div className='filter-table'>
        <Table size='sm' borderless>
          <thead>
            <tr>
              {SAMPLE_OPTIONS.map(header => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map(row => (
              <tr key={JSON.stringify(row)}>
                {SAMPLE_OPTIONS.map(option => (
                  <td key={option}>{row[option]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

const makeCommand = (command: string[]): ReactNode[] =>
  command.flatMap((token, i) => [' + ', <strong key={i}>{token}</strong>]).slice(1);

interface FilterBarHelpProps {
  show: boolean;
  setShowHelp: (show: boolean) => void;
}

export const FilterBarHelp = ({ show, setShowHelp }: FilterBarHelpProps) => {
  // https://react-bootstrap.github.io/components/carousel/#controlled
  const [index, setIndex] = useState(0);

  return (
    <Modal
      size='lg'
      keyboard={false}
      show={show}
      onHide={() => setShowHelp(false)}
      dialogClassName='filter-bar-howto'
    >
      <Modal.Header closeButton>
        <Modal.Title>How To Use The Filter Bar</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='carousel-container'>
          <InteractiveDiv
            className='carousel-control'
            onClick={() => {
              setIndex(Math.max(0, index - 1));
            }}
          >
            {index > 0 && (
              <>
                <MdChevronLeft />
                <Button size={Size.LG} appearance={Appearance.OUTLINED}>
                  Back
                </Button>
              </>
            )}
          </InteractiveDiv>
          <div className='carousel-interior'>
            <Carousel
              interval={null}
              controls={false}
              slide={false}
              activeIndex={index}
              onSelect={setIndex}
            >
              <Carousel.Item>
                <h3>Basic</h3>
                <p>
                  Type <strong>NBCFEM</strong> and hit enter (the filter bar is always
                  case-insensitive). This will give you only the &quot;NBCFEM&quot; lines.
                </p>
                <FilterDemo data={SAMPLE_DATA} />
                <p>
                  Press the &quot;x&quot; next to NBCFEM in the filter bar, or hit backspace, to
                  remove the filter.
                </p>
                <p>
                  Type <strong>NBC</strong> and hit enter. This will give you all (and only) the
                  lines with networks that include &quot;NBC&quot;.
                </p>
              </Carousel.Item>
              <Carousel.Item>
                <h3>Basic: Multiple Filters</h3>
                <p>
                  Type <strong>comedy</strong> and hit enter. Then type <strong>Sale</strong> and
                  hit enter. This gives you only &quot;comedy&quot; networks with the
                  &quot;Sale&quot; creative.
                </p>
                <FilterDemo data={SAMPLE_DATA} />
                <p>
                  You can press the &quot;x&quot; next to either &quot;NBC&quot; or &quot;Sale&quot;
                  in the filter bar. This will remove that filter and leave the other. You can also
                  hit the &quot;x&quot; on the far-right side of the filter bar to clear all
                  filters.
                </p>
              </Carousel.Item>
              <Carousel.Item>
                <h3>Advanced</h3>
                <p>
                  Click inside the filter bar and start typing <strong>Network</strong>. Once
                  it&apos;s highlighted in the dropdown you can hit enter. Do this again for{' '}
                  <strong>is</strong>, and finally for <strong>MTV</strong>. Click outside the
                  filter bar, hit &quot;Esc&quot; on your keyboard, or click &quot;Filter&quot;.
                </p>
                <FilterDemo advanced data={SAMPLE_DATA} />
                <p>
                  In the basic bar you cannot get &quot;MTV&quot; without &quot;MTV2&quot;. In the
                  advanced bar you can be more precise. You can use the backspace, enter key, and
                  the &quot;clear all&quot; button as you can in the basic bar.
                </p>
              </Carousel.Item>
              <Carousel.Item>
                <h3>Advanced: Or</h3>
                <p>
                  Enter {makeCommand(['Network', 'is', 'MTV'])} like before. You can click in the
                  dropdown, use the arrow keys and the enter key, or start typing to pare down the
                  dropdown list. Add {makeCommand(['or', 'Network', 'is', 'NBCFEM'])}.
                </p>
                <FilterDemo advanced data={SAMPLE_DATA} />
                <p>
                  In the basic bar, each row must satisfy all filters. In the advanced bar, when you
                  use <strong>or</strong>, lines only need to match one of the criteria. You can
                  chain as many <strong>or</strong> filters as you want. You can also use{' '}
                  <strong>and</strong>, which requires both to match.
                </p>
              </Carousel.Item>
              <Carousel.Item>
                <h3>Advanced: Not</h3>
                <p>Enter {makeCommand(['Network', 'is not', 'MTV2'])}</p>
                <FilterDemo advanced data={SAMPLE_DATA} />
                <p>
                  This will return every line with a network that is <em>not</em> &quot;MTV2&quot;.
                </p>
                <p>
                  You can chain this with &quot;or&quot; and &quot;and&quot;. Try{' '}
                  {makeCommand(['Network', 'is', 'MTV', 'and', 'Creative', 'is not', 'Sale'])}. This
                  gives you everything from &quot;MTV&quot;, without lines with the &quot;Sale&quot;
                  creative.
                </p>
              </Carousel.Item>
              <Carousel.Item>
                <h3>Advanced: Is Like</h3>
                <p>
                  Enter {makeCommand(['Network', 'is like'])}, then type <strong>NBC</strong> and
                  hit enter.
                </p>
                <FilterDemo advanced data={SAMPLE_DATA} />
                <p>
                  This works like the basic bar: the text only needs to match, not equal, the
                  entered text. However, here you confine your filter to one column. If you are
                  running on &quot;Drama&quot; networks, but with a creative called
                  &quot;Drama&quot;, this is essential.
                </p>
                <p>
                  You can also use <strong>is not like</strong> to get a negative match.
                </p>
              </Carousel.Item>
              <Carousel.Item>
                <h3>Advanced: Parentheses</h3>
                <p>
                  Enter{' '}
                  {makeCommand([
                    '(',
                    'Network',
                    'is',
                    'MTV',
                    'and',
                    'Creative',
                    'is',
                    'Sale',
                    ')',
                    'or',
                    '(',
                    'Network',
                    'is',
                    'NBCFEM',
                    'and',
                    'Creative',
                    'is',
                    'Celebrity',
                    ')'
                  ])}
                </p>
                <FilterDemo advanced data={SAMPLE_DATA} />
                <p>
                  Parentheses group logic together. This gives you every line that has MTV/Sale plus
                  every line that has NBCFEM/Celebrity.
                </p>
                <p>
                  Without parentheses, <strong>and</strong> and <strong>or</strong> will be applied
                  in order. With long and/or chains you&apos;ll probably need parentheses to get the
                  result you want.
                </p>
              </Carousel.Item>
              <Carousel.Item>
                <h3>Advanced: Parentheses Continued</h3>
                <p>
                  Try to get lines with the &quot;Celebrity&quot; creative but only for
                  &quot;HULUCOMEDY&quot; and &quot;MTV&quot; with
                  {makeCommand([
                    'Creative',
                    'is',
                    'Celebrity',
                    'and',
                    'Network',
                    'is',
                    'HULUCOMEDY',
                    'or',
                    'Network',
                    'is',
                    'MTV'
                  ])}
                  .
                </p>
                <FilterDemo advanced data={SAMPLE_DATA} />
                <p>
                  This gives you &quot;MTV/Sale&quot; because it took all the lines with
                  &quot;Celebrity&quot; and &quot;HULUCOMEDY&quot;, <em>then</em> it looked for any
                  other lines with &quot;Network&quot; as &quot;MTV&quot;. The proper filter would
                  be{' '}
                  {makeCommand([
                    'Creative',
                    'is',
                    'Celebrity',
                    'and',
                    '(',
                    'Network',
                    'is',
                    'HULUCOMEDY',
                    'or',
                    'Network',
                    'is',
                    'MTV',
                    ')'
                  ])}
                </p>
              </Carousel.Item>
            </Carousel>
          </div>
          <InteractiveDiv
            className='carousel-control'
            onClick={() => {
              setIndex(Math.min(NUM_SLIDES - 1, index + 1));
            }}
          >
            {index < NUM_SLIDES - 1 && (
              <>
                <MdChevronRight />
                <Button size={Size.LG} appearance={Appearance.OUTLINED}>
                  Next
                </Button>
              </>
            )}
          </InteractiveDiv>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => setShowHelp(false)}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};
